"use client";

import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { Section } from "@/components/ui/Section";
import { AmbientCanvas } from "@/components/ui/AmbientCanvas";
import { VideoBackground } from "@/components/ui/VideoBackground";
import { Typewriter } from "@/components/ui/Typewriter";
import { SectionSeparator } from "@/components/ui/SectionSeparator";

export function Hero() {
  const t = useTranslations("Hero");

  return (
    <Section
      id="home"
      noContainer
      noPadding
      className="relative overflow-hidden bg-(--bg-primary)"
      contentClassName="relative z-0 min-h-[calc(100vh-6rem)] flex items-center"
    >
      {/* <div className="absolute inset-0 z-0 bg-gradient-to-r from-[var(--bg-primary)] via-[var(--bg-primary)]/80 to-[var(--bg-primary)]/20 pointer-events-none" /> */}

      {/* Layer 1: Volumetric Base (The Void Sphere) - Always visible as foundation/fallback */}
      <AmbientCanvas />

      <div className="relative z-10 w-full max-w-400 mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 min-h-[80vh]">
        {/* Left Zone: The Statement */}
        <div className="lg:col-span-7 flex flex-col gap-6 px-4 sm:pr-0 lg:pl-12 py-4 sm:pb-0 lg:pt-16">
          {/* New Heading: "HI, I'M DEV DJ." */}
          <div className="overflow-hidden">
            <motion.h1
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              transition={{
                duration: 0.8,
                ease: [0.16, 1, 0.3, 1],
                delay: 0.2,
              }}
              className="font-bold font-outfit text-(--text-primary) tracking-tight leading-[1.05] text-[clamp(2.5rem,5.5vw,7.5rem)] md:text-[clamp(3.5rem,6vw,8rem)]"
            >
              {t("heading")}
            </motion.h1>
          </div>

          {/* Typed Text */}
          {/* Dynamic Output - Experience Focused */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex items-center gap-2 text-xl md:text-2xl font-mono text-(--text-secondary) font-medium"
          >
            <span className="text-(--brand-primary)">&gt;</span>
            <Typewriter
              words={t.raw("typedWords") as string[]}
              className="text-(--brand-primary)"
              cursorClassName="bg-[var(--brand-primary)]"
              typingSpeed={100}
              deletingSpeed={50}
              pauseTime={2000}
            />
          </motion.div>

          {/* Subtitle & CTA - Architecture details */}
          <div className="flex flex-col gap-8 max-w-xl relative mt-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              className="self-start"
            >
              <a
                href="#portfolio"
                className="group relative inline-flex items-center gap-3 px-8 py-4 bg-(--text-primary) text-(--bg-primary) rounded-full text-sm uppercase tracking-widest font-bold overflow-hidden hover:opacity-90 transition-opacity"
              >
                <span className="relative z-10">{t("ctaButton")}</span>
              </a>
            </motion.div>
          </div>
        </div>

        {/* Right Zone: Visual Anchor (Video) */}
        <div className="hidden lg:block lg:col-span-5 h-[80vh] relative flex items-center justify-center self-center">
          {/* 3D Video Container - Free Floating */}
          <motion.div
            animate={{
              y: [0, -20, 0],
              rotate: [0, 2, 0],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="relative w-full aspect-square"
          >
            <VideoBackground className="w-full h-full inset-0" />
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator - Minimal */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-12 right-12 flex flex-col items-center gap-4 mix-blend-difference z-10"
      >
        <motion.span
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="text-[10px] font-mono uppercase tracking-[0.3em] text-(--text-secondary) [writing-mode:vertical-rl]"
        >
          Scroll
        </motion.span>
        <div className="w-px h-24 bg-(--text-secondary)/20 relative overflow-hidden rounded-full">
          <motion.div
            animate={{ top: ["-100%", "100%"] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "linear",
            }}
            className="absolute w-full h-1/2 bg-linear-to-b from-transparent via-(--text-secondary) to-transparent"
          />
        </div>
      </motion.div>

      <motion.div className="absolute inset-0 bottom-0 z-0">
        <SectionSeparator
          position="bottom"
          variant="layered-wave"
          className="absolute bottom-0"
          fillClassName="fill-[var(--bg-primary)]"
        />
      </motion.div>
    </Section>
  );
}
